import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
  firmSettingsSchema,
  type FirmSettingsView,
  type ThemeId,
} from "@themislex/shared";
import { ApiError, api } from "../../api/client";
import { useApi } from "../../api/hooks";
import { useTheme } from "../../theme/ThemeContext";
import {
  Button,
  Card,
  ErrorNote,
  Input,
  PageHeader,
  Spinner,
  Textarea,
} from "../../components/ui";

interface ThemePreset {
  id: ThemeId;
  name: string;
  description: string;
  swatches: string[];
}

const THEME_PRESETS: ThemePreset[] = [
  {
    id: "chambers",
    name: "Chambers Classic",
    description: "Warm ivory, serif headings and a burgundy seal — the default.",
    swatches: ["#f3f0eb", "#29251f", "#6d2a35"],
  },
  {
    id: "minimal",
    name: "Modern Minimal",
    description: "Crisp neutrals with a teal accent and a quiet sans-serif.",
    swatches: ["#f8f9fa", "#1a202c", "#0f766e"],
  },
  {
    id: "dark",
    name: "Focus Dark",
    description: "A low-light variant of Modern Minimal for late hours.",
    swatches: ["#10141a", "#e6eaf0", "#209a94"],
  },
];

/** Snapshot key: the form remounts only when the stored settings change. */
function settingsKey(settings: FirmSettingsView): string {
  return [
    settings.firmName,
    settings.address,
    settings.gstin,
    settings.upiId,
    settings.invoicePrefix,
    settings.theme,
  ].join("|");
}

export default function Settings() {
  const { data, error, loading } = useApi<{ settings: FirmSettingsView }>(
    "/api/firm-settings",
  );

  const [qrFile, setQrFile] = useState<File | null>(null);
  const [qrBusy, setQrBusy] = useState(false);
  const [qrError, setQrError] = useState<string>();
  const [qrRevision, setQrRevision] = useState(0);
  const [removeBusy, setRemoveBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-muted">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (error || !data) {
    return <ErrorNote>{error ?? "Settings could not be loaded."}</ErrorNote>;
  }

  const settings = data.settings;

  const onQrFile = (e: ChangeEvent<HTMLInputElement>) => {
    setQrError(undefined);
    const file = e.target.files?.[0] ?? null;
    setQrFile(file);
  };

  const uploadQr = async () => {
    if (!qrFile) return;
    setQrBusy(true);
    setQrError(undefined);
    try {
      const body = new FormData();
      body.append("file", qrFile);
      await api("/api/firm-settings/qr", { method: "POST", body });
      setQrFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setQrRevision((n) => n + 1);
    } catch (err) {
      setQrError(err instanceof Error ? err.message : "Could not upload the QR image.");
    } finally {
      setQrBusy(false);
    }
  };

  const removeQr = async () => {
    setRemoveBusy(true);
    setQrError(undefined);
    try {
      await api("/api/firm-settings/qr", { method: "DELETE" });
      setQrRevision((n) => n + 1);
    } catch (err) {
      setQrError(err instanceof Error ? err.message : "Could not remove the QR image.");
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Chambers settings"
        subtitle="Firm identity, payment details and portal appearance."
      />

      <div className="mx-auto max-w-4xl space-y-6">
        <SettingsForm key={settingsKey(settings)} initial={settings} />

        <Card title="Stored UPI QR image (optional)">
          <p className="mb-4 text-xs text-muted">
            If your bank provides a branded QR image, upload it here and it will
            be embedded on every invoice PDF instead of the generated one. PNG or
            JPEG, up to 2&nbsp;MB. Re-open any invoice after changing this to regenerate
            its stored PDF.
          </p>
          {qrError && (
            <div className="mb-4">
              <ErrorNote>{qrError}</ErrorNote>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-6">
            {settings.hasQrImage ? (
              <img
                key={qrRevision}
                src={`/api/firm-settings/qr?v=${qrRevision}`}
                alt="Stored UPI QR image"
                className="h-28 w-28 rounded-md border border-border object-contain p-1"
              />
            ) : (
              <div className="flex h-28 w-28 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted">
                No image stored
              </div>
            )}
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                aria-label="QR image file (PNG or JPEG, max 2 MB)"
                className="block text-sm"
                onChange={onQrFile}
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  loading={qrBusy}
                  disabled={!qrFile}
                  onClick={() => void uploadQr()}
                >
                  Upload QR image
                </Button>
                {settings.hasQrImage && (
                  <Button variant="danger" loading={removeBusy} onClick={() => void removeQr()}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}

function SettingsForm({ initial }: { initial: FirmSettingsView }) {
  const { setTheme } = useTheme();
  const [form, setForm] = useState(() => ({
    firmName: initial.firmName,
    address: initial.address ?? "",
    gstin: initial.gstin ?? "",
    upiId: initial.upiId ?? "",
    invoicePrefix: initial.invoicePrefix,
    theme: initial.theme,
  }));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorNote, setErrorNote] = useState<string>();
  const [savedNote, setSavedNote] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setErrorNote(undefined);
    setFieldErrors({});
    setSavedNote(false);

    const parsed = firmSettingsSchema.safeParse(form);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setBusy(true);
    try {
      await api("/api/firm-settings", { method: "PUT", json: parsed.data });
      setTheme(parsed.data.theme);
      setSavedNote(true);
      setTimeout(() => setSavedNote(false), 4000);
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorNote(err.message);
        if (err.issues) {
          const errors: Record<string, string> = {};
          for (const issue of err.issues) {
            if (!errors[issue.path]) errors[issue.path] = issue.message;
          }
          setFieldErrors(errors);
        }
      } else {
        setErrorNote("Could not save the settings.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} noValidate className="space-y-6">
      {errorNote && (
        <div>
          <ErrorNote>{errorNote}</ErrorNote>
        </div>
      )}
      {savedNote && (
        <p
          className="rounded-md px-4 py-3 text-sm"
          style={{
            backgroundColor: "rgb(var(--tl-success-bg))",
            color: "rgb(var(--tl-success-fg))",
          }}
          role="status"
        >
          Settings saved.
        </p>
      )}

      <Card title="Firm identity">
        <p className="mb-4 text-xs text-muted">
          These details appear on the portal, the login screen and every invoice
          PDF.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Firm name"
            required
            value={form.firmName}
            onChange={(e) => setForm({ ...form, firmName: e.target.value })}
            error={fieldErrors.firmName}
          />
          <Input
            label="GSTIN (optional)"
            placeholder="22AAAAA0000A1Z5"
            value={form.gstin}
            onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
            error={fieldErrors.gstin}
          />
          <div className="sm:col-span-2">
            <Textarea
              label="Chambers address (optional)"
              rows={3}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              error={fieldErrors.address}
            />
          </div>
        </div>
      </Card>

      <Card title="Payments & invoicing">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="UPI ID (for invoice QR codes)"
            placeholder="chambers@upi"
            value={form.upiId}
            onChange={(e) => setForm({ ...form, upiId: e.target.value })}
            error={fieldErrors.upiId}
            hint="Every invoice PDF embeds a permanent UPI QR for this ID."
          />
          <Input
            label="Invoice number prefix"
            required
            value={form.invoicePrefix}
            onChange={(e) => setForm({ ...form, invoicePrefix: e.target.value })}
            error={fieldErrors.invoicePrefix}
            hint="Invoice numbers continue as PREFIX-0001, PREFIX-0002…"
          />
        </div>
      </Card>

      <Card title="Portal appearance">
        <p className="mb-4 text-xs text-muted">
          The selected theme applies for every visitor — clients included — and is
          saved with these settings.
        </p>
        <div className="grid gap-4 sm:grid-cols-3" role="radiogroup" aria-label="Theme">
          {THEME_PRESETS.map((preset) => {
            const selected = form.theme === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`card p-4 text-left transition-colors ${
                  selected ? "outline outline-2 outline-offset-2" : ""
                }`}
                style={
                  selected ? { outlineColor: "rgb(var(--tl-accent-text))" } : undefined
                }
                onClick={() => {
                  const next = { ...form, theme: preset.id };
                  setForm(next);
                  setTheme(preset.id);
                }}
              >
                <span className="flex items-center gap-2">
                  {preset.swatches.map((color) => (
                    <span
                      key={color}
                      className="inline-block h-4 w-4 rounded-full border border-border"
                      style={{ backgroundColor: color }}
                      aria-hidden="true"
                    />
                  ))}
                </span>
                <span className="mt-3 block font-heading text-base font-semibold">
                  {preset.name}
                </span>
                <span className="mt-1 block text-xs text-muted">{preset.description}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-6 flex justify-end">
          <Button type="submit" variant="primary" loading={busy}>
            Save settings
          </Button>
        </div>
      </Card>
    </form>
  );
}
